import { t } from '@hana/i18n';
import './CostDetail.i18n';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  IconActivity, IconAlertTriangle, IconCalendar, IconChartBar, IconChartPie, IconChecks, IconChevronDown, IconChevronLeft, IconCircleCheck, IconDots,
  IconBell, IconEye, IconFileText, IconGitMerge, IconSend, IconWallet, IconInfoCircle, IconLink, IconLock, IconShieldCheck, IconTable, IconTag, IconTruck, IconUser, IconUsers, IconCheck, IconArrowUpRight,
  type Icon,
} from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, StateView, useToast, type ScreenState } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from './access';
import { ACC_AR, ACC_CA, ACC_CP, CA, COSTING_DETAIL_DEFAULT, LOCK_AT, costingByNo, usd } from './costing';
import type { CostingCalc, CostingRow, CostingWarn, MatchKind } from './costingTypes';
import { f1 } from './view';
import { useTablet } from './useWide';
import s from './costDetail.module.css';

/** Icon nút hành động của dòng cảnh báo (theo board Mint-Cost-Detail). */
const ACT_ICON: Record<string, Icon> = { 'Gộp': IconGitMerge, 'Gửi duyệt': IconSend, 'Xin duyệt giá': IconSend, 'Nạp phí': IconWallet, 'Nhắc đối tác': IconBell, 'Áp VCB': IconCheck, 'Duyệt lệch': IconCheck };

// Mint-Cost › Chi tiết Costing theo Job (view=detail, 1536). Mọi số là TIỀN → sau quyền `money` (route đã gói). Chỉ đọc; nút ghi báo toast.

const STATUS_LABEL = { draft: 'Nháp', pend: 'Chờ duyệt', appr: 'Đã duyệt', lockp: 'Đã khoá kỳ' } as const;
const DOT: Readonly<Record<string, string>> = { ok: 'var(--d-green)', okd: 'var(--d-green)', diff: 'var(--d-amber)', none: 'var(--d-gray)', sent: 'var(--d-green)', draft: 'var(--d-amber)' };
const DEB = { sent: 'Debit đã gửi khách', draft: 'Debit nháp · chưa gửi', none: 'Chưa lên debit' } as const;
const DST: Readonly<Record<MatchKind, string>> = { ok: 'Khớp', okd: 'Lệch · ACC-CP đã duyệt', diff: 'Lệch', none: 'Chưa có' };

const HINT = {
  sum: 'Dự kiến = theo báo giá; Thực tế = debit bán − HĐ mua vào − chi không HĐ · chip Δ so báo giá',
  bm: 'Gom theo dịch vụ · chấm Debit (xanh đã gửi · cam nháp · xám chưa lên) / DN đối tác / HĐ mua vào (xanh khớp · cam lệch · xám chưa có) · tr ₫ · Tệ hiện nguyên tệ, rê chuột xem tỷ giá',
  match: 'DN đối tác ↔ dòng mua (lệch ≥ 0,05 tr ₫ do ACC-CP duyệt, QĐ 103) · HĐ mua vào ↔ dòng mua (thiếu sau Finish 7 ngày thì nhắc đối tác, QĐ 5)',
  act: 'Thao tác trên trang ghi vào đầu dòng thời gian (logAction COSTING)',
  w: 'Kiểm mỗi lần nạp phí, DN / HĐ về, đổi tỷ giá: lỗ thực tế (đỏ) / dự kiến lỗ (cam) · bán dưới giá mua · mua chưa có phí bán · lệch DN · lệch / thiếu HĐ mua vào · dòng trùng · tỷ giá lệch VCB · trễ duyệt costing (hạn từ mốc Finish job: thuần cước 5, có plus services 30 ngày làm việc)',
  info: 'Khách, tuyến, báo giá, PIC và mốc Finish lấy từ Job (BOOKINGDATA) · rê chuột lên dòng Finish để xem hạn duyệt costing',
  appr: 'Duyệt theo ghế: Lập → ACC-CP → ACC-CA (chỉ khi chi ≥ 200 tr) → ACC-CA khoá kỳ. Cần Lock FRT + Lock INV. Sửa sau duyệt về Chờ duyệt.',
  ppl: 'Hai nguồn, gộp và bỏ trùng: (1) theo khách: Sale, CS, Sale Asst, Kế toán ở hồ sơ khách (CUSDATA); (2) nhận xử lý / được giao trên chính bản ghi (TblOrgJD): Kế toán chi phí [ACC-CP] đứng đầu; người duyệt theo ghế ở cuối. Chưa có người hiện Chưa giao.',
  share: 'profit-share: lãi lô đã duyệt × hệ số vai trò (PIC × PICRating; có Sale Assistant thì PIC × (PICRating − NominateRating) và Assist × NominateRating, tổng 1,0) · tiền share theo kỳ ở báo cáo Profit Share',
  links: 'Mở bản ghi liên quan trong Ezy',
} as const;

function Section({ id, title, label, icon: Ico, hint, count, end, open, onToggle, children }: { id: string; title: string; label?: string; icon: Icon; hint: string; count?: ReactNode; end?: ReactNode; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <section className={s.sec} aria-label={label ?? title} data-sec={id}>
      <div className={s.sh}>
        <span className={s.sIco}><Ico size={18} stroke={1.9} aria-hidden="true" /></span>
        <h2>{title}</h2>
        <span className={s.info} role="img" aria-label={`Giải thích: ${hint}`} title={hint}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
        {count}
        <span className={s.shEnd}>
          {end}
          <button type="button" className={`${s.tog} ${open ? '' : s.togClosed}`} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={onToggle}>
            <IconChevronDown size={18} aria-hidden="true" />
          </button>
        </span>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

/** Mẫu CUSDATA theo hash tên khách (Mint-PIC kit): chọn CS / Kế toán minh hoạ. */
const MPIC_CUS = [
  { cs: 'MAITTN', acc: 'LIENNTK' }, { cs: 'ThuyNTT', acc: 'LIENNTK' }, { cs: 'NGOCNTB', acc: 'LIENNTK' }, { cs: 'HOATTN', acc: 'DuyenTM' },
] as const;
function cusOf(name: string) {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 9973;
  return MPIC_CUS[h % MPIC_CUS.length]!;
}
function peopleOf(c: CostingCalc): { who: string; role: string; hot: boolean }[] {
  const j = c.job;
  const cus = cusOf(j.cus);
  const raw: [string, string][] = [
    ['Kế toán chi phí', ACC_CP.replace(/^\[[^\]]*\]\s*/, '')], ['Sale', j.pic], ['CS', cus.cs], ['Sale Asst', j.sa], ['Kế toán', cus.acc],
    ['Duyệt chi phí', ACC_CP], ['Duyệt ≥ 200 tr · khoá kỳ', ACC_CA], ['Mở khoá costing', ACC_AR],
  ];
  const rows: { who: string; roles: string[] }[] = [];
  for (const [role, who] of raw) {
    const same = who && rows.find((r) => r.who.toUpperCase() === who.toUpperCase());
    if (same) same.roles.push(role);
    else rows.push({ who: who || 'Chưa giao', roles: [role] });
  }
  return rows.map((r, i) => ({ who: r.who, role: r.roles.join(' · '), hot: i < 2 }));
}
const ini = (w: string): string => (w.replace(/\[[^\]]*\]/g, ' ').replace(/ghế trống →|Acting|BOD-DIR/g, ' ').trim().split(/\s+/).pop() || '?').slice(0, 2).toUpperCase();

const curOf = (x: readonly [string, number, string, number, ...unknown[]]): string => (x[2] === 'VND' ? 'VND' : `${usd((x[1] * 1e6) / x[3])} ${x[2]}`);

export function CostDetail() {
  const toast = useToast();
  const nav = useNavigate();
  const access = useFinanceAccess();
  const params = useParams({ strict: false }) as { job?: string };
  const jobNo = params.job ?? COSTING_DETAIL_DEFAULT;
  const c = costingByNo(jobNo);
  const tablet = useTablet();
  const [shut, setShut] = useState<Record<string, boolean>>({ links: true, ...(tablet ? { ppl: true } : {}) });
  const [hi, setHi] = useState<string | null>(null);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const sec = (k: string) => ({ open: !shut[k], onToggle: () => setShut((p) => ({ ...p, [k]: !p[k] })) });
  const seek = (sel: string) => window.setTimeout(() => document.querySelector(sel)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);

  const accessState: ScreenState = financeScreenState(access);
  const state: ScreenState = accessState === 'ready' && !c ? 'empty' : accessState;
  const ready = state === 'ready' && !!c;
  const back = () => void nav({ to: '/ezy/cost', search: (p: Record<string, unknown>) => p });

  return (
    <Page kind="table">
      <PageHeader findPad
        title={jobNo}
        lead={
          <button type="button" className={s.back} aria-label={tablet ? 'Quay lại Costing' : 'Quay lại Costing theo Job'} title={tablet ? 'Quay lại Costing' : 'Quay lại Costing theo Job'} onClick={back}>
            <IconChevronLeft size={16} stroke={2.2} aria-hidden="true" />
          </button>
        }
        meta={
          c ? (
            <span className={`${s.chip} ${s[`st_${c.job.s}`]}`} title={c.job.s === 'lockp' ? `Kỳ ${c.job.per} đã khoá` : STATUS_LABEL[c.job.s]}>
              <i aria-hidden="true" />
              {STATUS_LABEL[c.job.s]}
            </span>
          ) : undefined
        }
        findHint={false}
        actions={
          <>
            {!tablet && <HeaderTool label="Phân tích · Duyệt · Việc" icon={IconChartBar} n={8} onClick={() => toast({ msg: 'Bảng phân tích chưa mở ở bản này' })} />}
            {state !== 'noperm' && <HeaderTool label="Thao tác costing" icon={IconDots} onClick={() => toast({ msg: 'Thao tác costing chưa mở ở bản này' })} />}
            <Button variant="primary" icon={tablet ? undefined : <IconSend size={16} aria-hidden="true" />} onClick={noWrite}>{state === 'noperm' ? 'Xin quyền' : c?.priLabel ?? 'Gửi duyệt'}</Button>
          </>
        }
      />
      <StateView
        state={state}
        title={state === 'empty' ? 'Không tìm thấy job' : undefined}
        icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => location.reload()}>{t('cdt.1')}</Button> : undefined}
      >
        {ready && c && <Body c={c} tablet={tablet} sec={sec} hi={hi} setHi={setHi} seek={seek} toast={(m) => toast({ msg: m })} />}
      </StateView>
    </Page>
  );
}

function Body({ c, tablet, sec, hi, setHi, seek, toast }: { c: CostingCalc; tablet: boolean; sec: (k: string) => { open: boolean; onToggle: () => void }; hi: string | null; setHi: (k: string | null) => void; seek: (sel: string) => void; toast: (m: string) => void }) {
  const j = c.job;
  const qG = c.qRev - c.qCost;
  const qM = c.qRev ? (qG / c.qRev) * 100 : 0;
  const kpi = ([['Doanh thu', c.rev, c.qRev, 1, 'tr ₫'], ['Chi phí', c.cost, c.qCost, -1, 'tr ₫'], ['Lãi gộp', c.gp, qG, 1, 'tr ₫'], ['Margin', c.mg, qM, 1, '%']] as const).map(([k, a, q, sg, u]) => {
    const mx = Math.max(Math.abs(a), Math.abs(q), 0.0001);
    const d = a - q;
    const same = Math.abs(d) < 0.05;
    const good = d * sg > -0.05;
    return { k, a, q, u, same, good, d, wq: Math.max(2, Math.round((Math.abs(q) / mx) * 100)), wa: Math.max(2, Math.round((Math.abs(a) / mx) * 100)), fa: a < 0 ? s.f_err : k === 'Chi phí' && d > 0.05 ? s.f_warn : s.f_a };
  });
  const wByRow = new Map<string, CostingWarn[]>();
  c.W.forEach((w) => { if (w.row) wByRow.set(w.row, [...(wByRow.get(w.row) ?? []), w]); });
  const see = (key: string) => { setHi(key); seek(`[data-cd-row="${key}"]`); };
  const B = c.P.filter((p) => p.buy);
  const big = c.cost >= CA;
  const ap = j.s === 'appr' || j.s === 'lockp';
  const cp = false;
  const steps = [
    { k: 'Lập', who: `[SALE] ${j.pic}`, done: j.s !== 'draft', cur: j.s === 'draft', skip: false },
    { k: 'Duyệt chi phí', who: ACC_CP, done: ap || cp, cur: j.s === 'pend' && !cp, skip: false },
    { k: 'Duyệt ≥ 200 tr', who: ACC_CA, done: big && ap, cur: j.s === 'pend' && cp && big, skip: !big },
    { k: `Khoá kỳ ${j.per}`, who: `[ACC-CA] ${j.s === 'lockp' ? 'đã khoá' : LOCK_AT}`, done: j.s === 'lockp', cur: j.s === 'appr', skip: false },
  ];
  const gates = ([['Lock FRT', !!j.g[0], 'Khoá cước bán (ASOrder)'], ['Lock INV', !!j.g[1], 'Khoá debit (DEBIT)']] as const);
  const share = c.gp > 0 ? [{ who: j.pic, role: 'PIC', r: j.pr, col: 'var(--d-blue)' }, ...(j.sa && j.pr < 1 ? [{ who: j.sa, role: 'Sale Assistant', r: 1 - j.pr, col: 'var(--d-violet)' }] : [])] : [];
  const act = j.act.slice(0, 6);
  const people = peopleOf(c);
  const meta: [Icon, string, string, string][] = [
    [IconUser, 'Khách', j.cus, 'Khách'], [IconTruck, 'Tuyến', j.rt, 'Tuyến'], [IconTag, 'Báo giá', j.qt || '—', 'Báo giá'], [IconUser, 'PIC · SA', j.pic + (j.sa ? ` · ${j.sa}` : ''), 'PIC · Sale Assistant'],
    [IconCircleCheck, 'Finish', j.fin || 'Chưa Finish', 'Finish job' + (c.deadline ? ` · ${c.deadline.tip}` : '')], [IconCalendar, 'Kỳ', j.per, 'Kỳ kế toán'],
  ];
  const links: [string, string, string, Icon][] = [
    ['Chi tiết Job', j.no, `/ezy/jobs/${j.no}`, IconTable], ['Hoá đơn · debit', j.cus, '/ezy/invoice', IconFileText], ['Phải trả', 'HĐ đối tác', '/ezy/ap', IconTable], ['Chia lợi nhuận', 'Profit Share', '/ezy/ap', IconChartPie],
  ];
  const wCount = (
    <>
      {c.wr > 0 && <span className={`${s.cnt} ${s.r}`} title="Mức đỏ"><i aria-hidden="true" />{c.wr}</span>}
      {c.wo > 0 && <span className={`${s.cnt} ${s.o}`} title="Mức cam"><i aria-hidden="true" />{c.wo}</span>}
    </>
  );

  const rowOf = (p: CostingRow) => {
    const ws = wByRow.get(p.key) ?? [];
    const lv = ws.some((w) => w.lv === 'r') ? 'r' : 'o';
    const flags: { k: string; tip: string; cls: string }[] = [];
    if (p.dup) flags.push({ k: 'Trùng', tip: 'Cùng mã + đối tác, lệch < 0,05 tr ₫', cls: s.flagR ?? '' });
    if (p.est) flags.push({ k: 'Dự kiến', tip: 'Dòng mua sinh từ báo giá · chờ DN đối tác', cls: s.flagB ?? '' });
    if (p.q && p.buy && p.bTr - p.q[1] > 0.04) flags.push({ k: `+${f1(p.bTr - p.q[1])} BG`, tip: `Chi vượt giá mua báo giá ${f1(p.q[1])} tr`, cls: '' });
    if (p.q && p.sell && p.q[0] - p.sTr > 0.04) flags.push({ k: `−${f1(p.q[0] - p.sTr)} BG`, tip: `Bán thấp hơn giá bán báo giá ${f1(p.q[0])} tr`, cls: '' });
    const lai = p.sell && p.buy ? p.sTr - p.bTr : p.sell ? p.sTr : -p.bTr;
    return (
      <div key={p.key} className={`${s.bmRow} ${hi === p.key ? s.hi : ''}`} data-cd-row={p.key}>
        <span title={p.name + (p.note ? ` · ${p.note}` : '')}><b>{p.code}</b></span>
        <span>{p.sell ? p.sell[0] : '—'}</span>
        <span className={s.num}>{p.sell ? f1(p.sTr) : '—'}</span>
        <span className={s.mut} title={p.sell ? (p.sell[2] === 'VND' ? `${usd(p.sell[1] * 1e6)} ₫` : `${curOf(p.sell)} × ${usd(p.sell[3])}`) : 'Chưa có dòng bán'}>{p.sell ? curOf(p.sell) : ''}</span>
        <span title={p.sell ? DEB[p.debit ?? 'none'] : 'Chưa có dòng bán'}><i className={s.dot} style={{ background: p.sell ? DOT[p.debit ?? 'none'] : DOT.none }} /></span>
        <span>{p.buy ? p.buy[0] : '—'}</span>
        <span className={s.num}>{p.buy ? f1(p.bTr) : '—'}</span>
        <span className={s.mut} title={p.buy ? (p.buy[2] === 'VND' ? `${usd(p.buy[1] * 1e6)} ₫` : `${curOf(p.buy)} × ${usd(p.buy[3])}`) : 'Chưa có dòng mua'}>{p.buy ? curOf(p.buy) : ''}</span>
        <span title={p.buy && p.dn ? `DN đối tác: ${DST[p.dn]}${p.buy[4] !== null ? ` · ${f1(p.buy[4])} tr` : ''}` : '—'}><i className={s.dot} style={{ background: p.buy && p.dn ? DOT[p.dn] : DOT.none }} /></span>
        <span title={p.buy && p.inv ? `HĐ mua vào: ${DST[p.inv]}${p.buy[5] !== null ? ` · ${f1(p.buy[5])} tr` : ''}` : '—'}><i className={s.dot} style={{ background: p.buy && p.inv ? DOT[p.inv] : DOT.none }} /></span>
        <span className={`${s.num} ${lai < -0.04 ? s.neg : ''}`}>{f1(lai)}</span>
        <span className={s.flags}>
          {ws.length > 0 && (
            <button type="button" className={`${s.wBtn} ${s[`wBtn_${lv}`]}`} aria-label="Xem cảnh báo của dòng" title={ws.map((w) => w.k).join(' · ')} onClick={() => { setHi(p.key); seek(`[data-cd-w="${ws[0]!.id}"]`); }}>
              {ws.length}
            </button>
          )}
          {p.lock && <span title="Đã lên debit gửi khách · giảm giá bằng debit thay thế" aria-label="Dòng khoá"><IconLock size={14} stroke={2} aria-hidden="true" /></span>}
          {flags.slice(0, 2).map((f) => <span key={f.k} className={`${s.flag} ${f.cls}`} title={f.tip}>{f.k}</span>)}
        </span>
      </div>
    );
  };

  const matchCard = (k: string, tip: string, pick: (p: CostingRow) => MatchKind | null, vi: 4 | 5, kind: 'dn' | 'inv') => {
    const cnt = { ok: 0, diff: 0, none: 0 };
    B.forEach((p) => { const m = pick(p) ?? 'none'; cnt[m === 'okd' ? 'ok' : m] += 1; });
    const n = B.length || 1;
    const items = B.filter((p) => pick(p) !== 'ok');
    return (
      <div className={s.mc} role="group" aria-label={k} key={k}>
        <div className={s.mcTop}>
          <b>{k}</b>
          <span className={s.info} role="img" aria-label={tip} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
          <span className={s.mcChips}>
            <span style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}>Khớp {cnt.ok}</span>
            <span style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }}>Lệch {cnt.diff}</span>
            <span style={{ background: 'var(--ctl)', color: 'var(--tx2)' }}>Thiếu {cnt.none}</span>
          </span>
        </div>
        <div className={s.seg}>
          {(['ok', 'diff', 'none'] as const).filter((m) => cnt[m] > 0).map((m) => (
            <div key={m} style={{ width: `${((cnt[m] / n) * 100).toFixed(1)}%`, background: DOT[m] }} title={`${m === 'ok' ? 'Khớp' : m === 'diff' ? 'Lệch' : 'Thiếu'} ${cnt[m]}`} />
          ))}
        </div>
        {items.length > 0 ? (
          <>
            <div className={`${s.mi} ${s.miHead}`}><span>{t('cdt.2')}</span><span>{t('cdt.3')}</span><span>{t('cdt.4')}</span><span>{t('cdt.5')}</span><span>Δ</span><span /><span /></div>
            {items.map((p) => {
              const m = pick(p) ?? 'none';
              const v = p.buy![vi];
              const nag = kind === 'inv' && m === 'none' && c.fin && (j.fd as number) > 7;
              const label = m === 'diff' ? 'Duyệt lệch' : nag ? 'Nhắc đối tác' : '';
              return (
                <div className={s.mi} key={p.key}>
                  <button type="button" className={s.lk} title={`Tới dòng ${p.code}`} onClick={() => see(p.key)}>{p.code}</button>
                  <span>{p.buy![0]}</span>
                  <span>{f1(p.bTr)}</span>
                  <span>{v === null ? '—' : f1(v)}</span>
                  <span>{v === null ? '' : `${v - p.bTr > 0 ? '+' : ''}${f1(v - p.bTr)}`}</span>
                  <span className={s.pill} style={m === 'diff' || nag ? { background: 'var(--warn-bg)', color: 'var(--warn)' } : undefined}>{m === 'diff' ? 'Lệch' : nag ? 'Quá 7 ngày' : 'Chờ'}</span>
                  <span>{label && <button type="button" className={`${s.act} ${s.actSm}`} onClick={() => toast('Chưa mở ghi ở bản này')}>{label}</button>}</span>
                </div>
              );
            })}
          </>
        ) : (
          <div className={s.allOk}>{t('cdt.6')}</div>
        )}
      </div>
    );
  };

  return (
    <div className={s.grid}>
      <div className={s.col}>
        <Section id="sum" title="Tóm tắt" icon={IconChartBar} hint={HINT.sum} {...sec('sum')}>
          <div className={s.kpis}>
            {kpi.map((k) => (
              <div className={s.kpi} role="group" aria-label={k.k} key={k.k}>
                <div className={s.kTop}>
                  <span>{k.k}</span>
                  <span className={`${s.delta} ${k.same ? s.d_same : k.good ? s.d_good : s.d_bad}`} title={k.k === 'Margin' ? `Thực tế ${f1(k.a)} % · báo giá ${f1(k.q)} %` : `So báo giá ${j.v2 ? j.qt : j.qt || '—'}: ${f1(k.q)} tr ₫`}>
                    {k.same ? '= BG' : `${k.d > 0 ? '+' : '−'}${f1(Math.abs(k.d))}${k.u === '%' ? ' đ' : ''}`}
                  </span>
                </div>
                <div className={s.kVal}><b className={k.a < 0 ? s.neg : undefined}>{f1(k.a)}</b><span>{k.u}</span></div>
                <div className={s.bars}>
                  <span title="Theo báo giá">{t('cdt.7')}</span><div className={s.track}><div className={s.f_q} style={{ width: `${k.wq}%` }} /></div><span>{f1(k.q)}</span>
                  <span>{t('cdt.8')}</span><div className={s.track}><div className={k.fa} style={{ width: `${k.wa}%` }} /></div><span>{f1(k.a)}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="bm" title="Bán | Mua theo mã phí" label="Bán và mua theo mã phí" icon={IconTable} hint={HINT.bm} count={<span className={s.shEnd} style={{ marginLeft: 0 }}>{c.P.length} dòng</span>} {...sec('bm')}>
          <div className={s.bm} style={{ margin: '0 -16px -16px' }}>
            <div className={`${s.bmHead2} ${s.band} ${s.bandTop}`}><span /><span className={s.bandSell}>{t('cdt.9')}</span><span className={s.bandBuy}>{t('cdt.10')}</span><span /><span /></div>
            <div className={`${s.bmHead2} ${s.band}`}>
              <span>{t('cdt.2')}</span><span>{t('cdt.11')}</span><span className={s.num}>tr ₫</span><span>{t('cdt.12')}</span><span title="Debit: xanh đã gửi khách · cam nháp · xám chưa lên debit">Debit</span>
              <span>{t('cdt.3')}</span><span className={s.num}>tr ₫</span><span>{t('cdt.12')}</span><span title="DN đối tác: xanh khớp · cam lệch · xám chưa về">DN</span><span title="HĐ mua vào: xanh khớp · cam lệch · xám chưa có">{t('cdt.13')}</span>
              <span className={s.num}>{t('cdt.14')}</span><span>{t('cdt.15')}</span>
            </div>
            {c.svc.map((g) => {
              const gg = g.ps.reduce((a, p) => a + p.sTr - p.bTr, 0);
              return (
                <div key={g.k}>
                  <div className={s.grp}><span>{g.k} <em>· {g.ps.length}</em></span><span className={gg < -0.04 ? s.neg : undefined}>{f1(gg)}</span></div>
                  {g.ps.map(rowOf)}
                </div>
              );
            })}
            <div className={s.foot}>
              <span>{t('cdt.16')}</span><span>{c.P.length} dòng</span><span className={s.num}>{f1(c.rev)}</span><span /><span /><span /><span className={s.num}>{f1(c.cost)}</span><span /><span /><span />
              <span className={`${s.num} ${c.gp < 0 ? s.neg : ''}`}>{f1(c.gp)}</span><span>tr ₫</span>
            </div>
          </div>
        </Section>

        {!tablet && <Section id="match" title="Khớp chứng từ" icon={IconChecks} hint={HINT.match} {...sec('match')}>
          <div className={s.mcards}>
            {matchCard('DN đối tác ↔ dòng mua', 'Lệch ≥ 0,05 tr ₫ do ACC-CP duyệt', (p) => p.dn, 4, 'dn')}
            {matchCard('HĐ mua vào ↔ dòng mua', 'Thiếu sau Finish 7 ngày thì nhắc đối tác', (p) => p.inv, 5, 'inv')}
          </div>
        </Section>}

        <Section id="act" title="Hoạt động" icon={IconActivity} hint={HINT.act} {...sec('act')}>
          <div className={s.tl}>
            {act.map(([at, who, k], x) => (
              <div className={s.tlRow} key={`${at}${k}`}>
                <span className={`${s.tlDot} ${x === 0 ? s.tlNow : ''}`} />
                <div><div className={s.tlK}>{k}</div><div className={s.tlMeta}>{at} · {who}</div></div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className={s.col}>
        <Section id="w" title="Cảnh báo & việc cần làm" label="Cảnh báo sớm" icon={IconAlertTriangle} hint={HINT.w} count={wCount} {...sec('w')}>
          {c.W.length === 0 ? (
            <div className={s.none}><IconCircleCheck size={16} aria-hidden="true" />{t('cdt.17')}</div>
          ) : (
            c.W.map((w) => (
              <div className={`${s.warnRow} ${s[`wr_${w.lv}`]}`} key={w.id} data-cd-w={w.id} title={w.tip} style={hi === w.row ? { background: w.lv === 'r' ? 'var(--err-bg)' : 'var(--warn-bg)' } : undefined}>
                <span className={`${s.wDot} ${s[`wd_${w.lv}`]}`} />
                <div className={s.wTx}>{w.k}{w.v && <small>{w.v}</small>}</div>
                <div className={s.wBtns}>
                  {w.row && <button type="button" className={`${s.act}`} aria-label="Xem dòng của cảnh báo" title="Xem dòng" onClick={() => see(w.row!)}><IconEye size={14} aria-hidden="true" />Xem</button>}
                  {w.act && <button type="button" className={s.act} onClick={() => toast('Chưa mở ghi ở bản này')}>{(() => { const I = ACT_ICON[w.act] ?? IconCheck; return <I size={14} aria-hidden="true" />; })()}{w.act}</button>}
                </div>
              </div>
            ))
          )}
        </Section>

        <Section id="info" title="Thông tin chính" icon={IconFileText} hint={HINT.info} {...sec('info')}>
          {meta.map(([Ico, lb, v, tip]) => (
            <div className={s.meta} key={lb} title={tip}><Ico size={15} stroke={1.9} aria-hidden="true" /><span>{lb}</span><span>{v}</span></div>
          ))}
        </Section>

        <Section id="appr" title="Duyệt & khoá theo ghế" label="Duyệt và khoá" icon={IconShieldCheck} hint={HINT.appr} {...sec('appr')}>
          <div className={s.gates}>
            {gates.map(([k, ok, tip]) => (
              <span key={k} className={`${s.gate} ${ok ? s.gate_ok : s.gate_miss}`} title={tip}>
                {k}
                {ok ? <IconCheck size={13} stroke={2.6} aria-hidden="true" /> : <button type="button" className={`${s.act} ${s.actSm}`} onClick={() => toast('Chưa mở ghi ở bản này')}>{t('cdt.18')}</button>}
              </span>
            ))}
            <span className={s.gate} title="Thu hồi HĐ đối tác (InvPoint) · dung sai 2 %">HĐ {c.invPt}%</span>
          </div>
          <div className={s.steps} role="list" aria-label="Các bước duyệt">
            {steps.map((st, x) => (
              <div className={s.step} role="listitem" key={st.k}>
                <div className={s.stepRail}>
                  <span className={`${s.stepNo} ${st.skip ? s.stepSkip : st.done ? s.stepDone : st.cur ? s.stepCur : ''}`}>{st.done && !st.skip ? <IconCheck size={14} stroke={2.6} aria-hidden="true" /> : x + 1}</span>
                  {x < 3 && <span className={`${s.stepLine} ${st.done ? s.stepLineDone : ''}`} />}
                </div>
                <div className={`${s.stepTx} ${st.skip ? s.stepSkipTx : ''}`}><b>{st.k}</b><div title={st.skip ? 'Bỏ qua · chi < 200 tr' : st.who}>{st.skip ? 'Bỏ qua · chi < 200 tr' : st.who}</div></div>
              </div>
            ))}
          </div>
          <div className={s.unlock}>
            <div className={s.row}><span>{t('cdt.19')}</span><span title="Mở khoá bắt buộc ghi lý do · ghế ACC-AR · sửa xong về Chờ duyệt"><IconLock size={13} aria-hidden="true" /> Mở khoá: {ACC_AR}</span></div>
            {j.hist.length ? j.hist.map(([at, who, k]) => <div className={s.row} key={at}><span>{k}</span><span>{at} · {who}</span></div>) : <div className={s.menuHint}>—</div>}
          </div>
        </Section>

        <Section id="ppl" title="Người phụ trách" icon={IconUsers} hint={HINT.ppl} {...sec('ppl')}>
          {people.map((p) => (
            <div className={s.person} key={p.role}>
              <span className={`${s.av} ${p.hot ? s.avHot : ''}`} title={p.who}>{ini(p.who)}</span>
              <div className={s.pTx}>{p.who}<small>{p.role}</small></div>
            </div>
          ))}
        </Section>

        {!tablet && <Section
          id="share" title="Chia lợi nhuận" icon={IconChartPie} hint={HINT.share} {...sec('share')}
          count={<span className={s.tag} style={ap ? { background: 'var(--ok-bg)', color: 'var(--ok)' } : { background: 'var(--sub)', color: 'var(--tx2)' }}>{ap ? 'Đã duyệt' : 'Tạm tính'}</span>}
          end={<b>{f1(c.gp)} <span>tr ₫</span></b>}
        >
          {share.length ? (
            <>
              <div className={s.shareBar}>{share.map((x) => <div key={x.role} style={{ width: `${x.r * 100}%`, background: x.col }} title={`${x.role} ${x.who} × ${f1(x.r).replace(',0', '')}`} />)}</div>
              {share.map((x) => <div className={s.shareRow} key={x.role}><i style={{ background: x.col }} /><b>{x.who}</b><span>{x.role} × {f1(x.r).replace(',0', '')}</span><b>{f1(c.gp * x.r)}</b></div>)}
            </>
          ) : (
            <div className={s.menuHint}>{t('cdt.20')}</div>
          )}
        </Section>}

        <Section id="links" title="Liên kết" icon={IconLink} hint={HINT.links} {...sec('links')}>
          {links.map(([k, sub, href, Ico]) => (
            <a className={s.link} href={href} key={k}><Ico size={15} stroke={1.9} aria-hidden="true" /><span>{k}</span><span>{sub}</span><IconArrowUpRight size={14} aria-hidden="true" /></a>
          ))}
        </Section>
      </div>
    </div>
  );
}
